import { Application } from "#/_Bootstrap/Application.ts";
import PopupManager from "../../Common/Popups/PopupManager";
import { showDialog } from "../Dialogs/DialogActions";

let checkingForAccess = false;
let needToCheckInTheFuture = true;

/**
 * Checks if we've been granted permission for the local network access permission.
 * Predominantly this is used by Linkly but it's made generic for any future uses.
 *
 * It returns false if we previously were denied (or unable to determine) and a page refresh is required.
 */
export const checkForLocalNetworkAccess = async (wasDenied = false): Promise<boolean> => {
    if(!needToCheckInTheFuture) {
        return true;
    }

    if(checkingForAccess) {
        console.error("Already checking for local network access");
        PopupManager.warning(
            "We're already checking for local network access, please provide access before continuing"
        );

        return false;
    }

    checkingForAccess = true;

    const permission = await navigator.permissions.query({
        name: "local-network-access" as PermissionName,
    });

    if(permission.state === "granted") {
        checkingForAccess = false;
        needToCheckInTheFuture = wasDenied;

        // If we were denied at one point, we need to do a full refresh to have access, Chrome provides the prompt
        // for us
        return !wasDenied;
    }

    const onPermissionChanged = new Promise(res => {
        permission.addEventListener("change", res, { once: true });
    });

    let canConnectToLocalNetwork = true;

    if(permission.state === "prompt") {
        // Only show the prompt to Chrome versions more than 142
        let chromeVersion = 150;

        try {
            const userAgent = await navigator.userAgentData?.getHighEntropyValues([ "fullVersionList" ]);

            if(userAgent?.fullVersionList) {
                for(const version of userAgent.fullVersionList) {
                    if(version.brand.includes("Google")) {
                        chromeVersion = parseInt(version.version.split(".")[0]);
                    }
                }
            }
        } catch {
            // Do nothing, can't detect Chrome version
        }

        if(chromeVersion >= 142) {
            PopupManager.info(
                "Shopfront needs access to local devices, please following the prompts on the dialog"
            );
        }

        const urlsToCheck = [
            "https://localhost:25683",
            "https://localhost:6001",
            "https://localhost",
            "http://localhost",
        ];

        let successfullyAccessed = false;

        for(const url of urlsToCheck) {
            try {
                const response = await fetch(url);

                if(response.status !== 0) {
                    checkingForAccess = false;
                    needToCheckInTheFuture = false;

                    return true;
                }

                successfullyAccessed = true;
                break;
            } catch {
                // Do nothing
            }
        }

        if(!successfullyAccessed) {
            console.error("Unable to attempt to connect to the local network");
            canConnectToLocalNetwork = false;
        }
    }

    if(permission.state === "denied" || !canConnectToLocalNetwork) {
        wasDenied = true;

        Application.get("Redux").dispatch(showDialog(
            "Local Network Access Required",
            <div>
                <p className="max-w-2xl">
                    Shopfront needs permission to access your local network. Without this some features (such as Linkly)
                    won&apos;t work. Please enable the permission by performing the following steps:
                </p>
                <ol className="list-decimal text-left max-w-xl mx-auto">
                    <li>Press the site configuration icon (next to the URL)</li>
                    <li>Toggle on <em>Local network access</em></li>
                    <li>Press the <em>Reload</em> button</li>
                </ol>
                <div className="relative w-min mx-auto">
                    <img
                        src="https://cdn.onshopfront.com/imgs/help/local-network-access.gif"
                        alt="Network Access Instructions"
                        width={491}
                        height={535}
                    />
                    <div
                        className="absolute text-left overflow-hidden text-ellipsis"
                        style={{
                            top       : 54,
                            left      : 155,
                            fontSize  : "0.8em",
                            background: "#edf2fa",
                            color     : "#373d43",
                            width     : "calc(100% - 155px)",
                        }}
                    >
                        {window.location.host}
                    </div>
                </div>
            </div>,
            "information",
            false,
            true,
            false,
            true
        ));
    }

    await onPermissionChanged;

    checkingForAccess = false;

    return checkForLocalNetworkAccess(wasDenied);
};
